import Image from 'next/image';
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { LinkIframeViewer } from '../previews/LinkIframeViewer';
import type { LinkMetadata } from './types';

const WWW_PREFIX_PATTERN = /^www\./i;

export function renderExcludedLink({
  isExcluded,
  url,
  className,
  children,
}: {
  isExcluded: boolean;
  url: string;
  className?: string;
  children?: ReactNode;
}): ReactNode | null {
  if (!isExcluded) {
    return null;
  }

  const display = (() => {
    try {
      const parsed = new URL(url);
      const host = parsed.hostname.replace(WWW_PREFIX_PATTERN, '');
      const path = parsed.pathname === '/' ? '' : parsed.pathname;
      return `${host}${path}`;
    } catch {
      return url;
    }
  })();

  return (
    <div className={className}>
      {children}
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-3 inline-block text-primary hover:underline break-words"
      >
        {display}
      </a>
    </div>
  );
}

export function renderSkippedPreview({
  showPreview,
  hasError,
  loading,
  metadata,
  url,
  children,
  isTwitterImage,
  resolvedImageIsTwitterImage,
  resolvedIsTweetUrl,
}: {
  showPreview: boolean;
  hasError: boolean;
  loading: boolean;
  metadata: LinkMetadata | null | undefined;
  url: string;
  children?: ReactNode;
  isTwitterImage: boolean;
  resolvedImageIsTwitterImage: boolean;
  resolvedIsTweetUrl: boolean;
}): ReactNode | undefined {
  const shouldSkip =
    !showPreview ||
    hasError ||
    !(loading || metadata) ||
    isTwitterImage ||
    resolvedImageIsTwitterImage ||
    resolvedIsTweetUrl;

  if (!shouldSkip) {
    return;
  }

  if (isTwitterImage || resolvedImageIsTwitterImage || resolvedIsTweetUrl) {
    return null;
  }

  return (
    <>
      {children || (
        <a href={url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
          {url}
        </a>
      )}
    </>
  );
}

export function renderTwitterImageCard({
  resolvedIsTwitterImage,
  metadata,
  url,
  className,
  children,
}: {
  resolvedIsTwitterImage: boolean;
  metadata: LinkMetadata | null | undefined;
  url: string;
  className?: string;
  children?: ReactNode;
}): ReactNode | null {
  if (!resolvedIsTwitterImage) {
    return null;
  }

  const pickMetadataImage = () => {
    if (typeof metadata?.image === 'string' && metadata.image.length > 0) {
      return metadata.image;
    }
    if (typeof metadata?.url === 'string' && metadata.url.length > 0) {
      return metadata.url;
    }
    return null;
  };
  const imgSource = pickMetadataImage() ?? url;
  const altText = typeof metadata?.title === 'string' && metadata.title.length > 0 ? metadata.title : 'Image';

  return (
    <div className={className}>
      {children}
      <div className="mt-3 relative overflow-hidden rounded-2xl border border-border/60 bg-card/95">
        <div className="relative aspect-[2/1] overflow-hidden bg-muted">
          <Image
            src={imgSource}
            alt={altText}
            fill
            className="object-contain bg-black/5 bg-muted/20"
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
            unoptimized
          />
        </div>
      </div>
    </div>
  );
}

export function renderIframeView({
  viewMode,
  className,
  isFullscreen,
  children,
  url,
  iframeUrl,
  isIframeLoading,
  iframeError,
  currentIframeTitle,
  faviconUrl,
  domain,
  onBack,
  onClose,
  onToggleFullscreen,
  onOpenInNewTab,
  onIframeLoad,
  onIframeError,
  metadataTitle,
}: {
  viewMode: 'preview' | 'iframe';
  className?: string | undefined;
  isFullscreen: boolean;
  children?: ReactNode | undefined;
  url: string;
  iframeUrl: string | null;
  isIframeLoading: boolean;
  iframeError: boolean;
  currentIframeTitle: string;
  faviconUrl?: string | undefined;
  domain: string;
  onBack: () => void;
  onClose: () => void;
  onToggleFullscreen: () => void;
  onOpenInNewTab: () => void;
  onIframeLoad: (title: string | undefined) => void;
  onIframeError: () => void;
  metadataTitle?: string | undefined;
}): ReactNode | null {
  if (viewMode !== 'iframe') {
    return null;
  }

  return (
    <div className={cn(className, isFullscreen ? 'fixed inset-0 z-layer-modal bg-background' : '')}>
      {children}
      <LinkIframeViewer
        url={url}
        iframeUrl={iframeUrl}
        isIframeLoading={isIframeLoading}
        iframeError={iframeError}
        isFullscreen={isFullscreen}
        currentIframeTitle={currentIframeTitle}
        faviconUrl={faviconUrl ?? null}
        domain={domain}
        onBack={onBack}
        onClose={onClose}
        onToggleFullscreen={onToggleFullscreen}
        onOpenInNewTab={onOpenInNewTab}
        onIframeLoad={onIframeLoad}
        onIframeError={onIframeError}
        metadataTitle={metadataTitle ?? null}
      />
    </div>
  );
}
